import Component from "vue-class-component";
import Vue from "vue";
import {ExportType} from "@/components/properties/settings/export/model/ExportType";
import ConfigurableSettingView from "@/views/settings/ConfigurableSettingView";
import {now} from "moment";
import {AXIOS} from "@/lib/http-common";
import UserActionResultSender from "@/components/application/UserActionResultSender";
import {ExportKey} from "@/components/properties/settings/export/model/ExportKey";
import Property from "@/components/properties/model/Property";
import ExportLayoutSettingsView from "@/views/settings/export/export-layout/ExportLayoutSettingsView";
import {AutoExportFrequency} from "@/components/properties/settings/export/model/AutoExportFrequency";
import ExportStaticFieldsView from "@/views/settings/export/static-fields/ExportStaticFieldsView";
import {ExportTimeFormat} from "@/components/properties/settings/export/model/ExportTimeFormat";
import ExportLayout from "@/components/properties/settings/export/model/ExportLayout";
import PatternBuilder from "@/views/settings/export/file-name-pattern/PatternBuilder.vue";
import {FileOutputMode} from "@/components/properties/settings/export/model/FileOutputMode";

@Component({
  methods: {now},
  components: {ExportStaticFieldsView, ExportLayoutSettingsView, ConfigurableSettingView, PatternBuilder},
  computed: {
    TimeFormatExport() {
      return ExportTimeFormat;
    },
    AutoExportFrequency() {
      return AutoExportFrequency;
    },
    ExportKey() {
      return ExportKey;
    },
    ExportType() {
      return ExportType;
    },
    FileOutputMode() {
      return FileOutputMode;
    }
  }
})
export default class ExportSettingsView extends Vue {
  public exportType: string = ExportType.JSON;
  public autoExportEnabled: boolean = false;
  public autoExportFrequency: string = AutoExportFrequency.AFTER_UPDATE;
  public exportTime: string | null = "";
  public webServiceOutputActivated = false;
  public fileOutputActivated = false;
  public destinationFolder: string = "";
  public fileOutputMode: string = FileOutputMode.OVERWRITE;
  public defaultExportPath: string = "";
  public loading: boolean = false;
  public databaseExportSettings!: Property[];
  public exportSettings!: Property[];
  public tab: number = 0;
  public isAmPmFormatEnabled: boolean = false;
  public exportLayoutFromDatabase: ExportLayout[] = [];
  public patternPreview: string = "";
  public databasePatternPreview: string = "";
  public showDialog: boolean = false;
  public separator = ";";
  public separatorOptions: SeparatorOption[] = [];

  public mounted() {
    this.getExportSettingsFromDatabase();
    this.separatorOptions = [
      {text: this.$t('export.csv.comma') as string, value: ","},
      {text: this.$t('export.csv.semicolon') as string, value: ";"},
      {text: this.$t('export.csv.tab') as string, value: "\\t"},
      {text: this.$t('export.csv.space') as string, value: " "},
    ];
  }

  public getExportSettingsFromDatabase() {
    this.loading = true;
    this.getDefaultDestinationFolder();
    AXIOS.post(`/properties/export`, Object.values(ExportKey))
      .then((message) => {
        this.databaseExportSettings = message.data;
        this.databasePatternPreview = this.getPropertyValue(ExportKey.EXPORT_FILE_PATTERN);
        this.patternPreview = this.getPropertyValue(ExportKey.EXPORT_FILE_PATTERN);
        this.separator = this.getPropertyValue(ExportKey.EXPORT_CSV_DELIMITER);
        this.setDefaultProperties();
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public setDefaultProperties() {
    this.exportType = this.getPropertyValue(ExportKey.EXPORT_TYPE);
    this.autoExportFrequency = this.getPropertyValue(ExportKey.FREQUENCY_AUTO_EXPORT);
    this.autoExportEnabled = this.getPropertyValue(ExportKey.AUTO_EXPORT_ENABLED) === "true";
    this.fileOutputActivated = this.getPropertyValue(ExportKey.EXPORT_TO_FILE) === "true";
    this.webServiceOutputActivated = this.getPropertyValue(ExportKey.EXPORT_WEBSERVICE) === "true";
    this.destinationFolder = this.getPropertyValue(ExportKey.EXPORT_FILE_DESTINATION);
    this.exportTime = this.getPropertyValue(ExportKey.EXPORT_TIME);
    this.isAmPmFormatEnabled = this.getPropertyValue(ExportKey.EXPORT_TIME_FORMAT) === "ampm";
    this.patternPreview = this.getPropertyValue(ExportKey.EXPORT_FILE_PATTERN);
    this.fileOutputMode = this.getPropertyValue(ExportKey.EXPORT_OUTPUT_MODE);
  }

  public saveExportType() {
    const propertyDto: Property = {propKey: ExportKey.EXPORT_TYPE.valueOf(), propValue: this.exportType};
    AXIOS.put("/properties/export", propertyDto)
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      });
  }

  public saveCsvDelimiter() {
    const propertyDto: Property = {propKey: ExportKey.EXPORT_CSV_DELIMITER.valueOf(), propValue: this.separator};
    AXIOS.put("/properties/export", propertyDto)
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      });
  }

  public saveProperties() {
    this.loading = true;
    this.destinationFolder = (this.destinationFolder !== null && this.destinationFolder !== '') ? this.destinationFolder : this.defaultExportPath;

    this.exportSettings = [
      {propKey: ExportKey.EXPORT_TYPE.valueOf(), propValue: this.exportType},
      {propKey: ExportKey.AUTO_EXPORT_ENABLED.valueOf(), propValue: this.autoExportEnabled.toString()},
      {propKey: ExportKey.FREQUENCY_AUTO_EXPORT.valueOf(), propValue: this.autoExportFrequency},
      {propKey: ExportKey.EXPORT_TO_FILE.valueOf(), propValue: this.fileOutputActivated.toString()},
      {propKey: ExportKey.EXPORT_OUTPUT_MODE.valueOf(), propValue: this.fileOutputMode},
      {propKey: ExportKey.EXPORT_WEBSERVICE.valueOf(), propValue: this.webServiceOutputActivated.toString()},
      {
        propKey: ExportKey.EXPORT_TIME_FORMAT.valueOf(),
        propValue: this.isAmPmFormatEnabled ? ExportTimeFormat.AmPm : ExportTimeFormat.H24
      }
    ];
    if (this.exportTime) {
      this.exportSettings.push({propKey: ExportKey.EXPORT_TIME.valueOf(), propValue: this.exportTime});
    }

    if (this.fileOutputActivated) {
      this.exportSettings.push({
        propKey: ExportKey.EXPORT_FILE_DESTINATION.valueOf(),
        propValue: this.destinationFolder
      });
    }
    if (this.patternPreview) {
      this.exportSettings.push({
        propKey: ExportKey.EXPORT_FILE_PATTERN.valueOf(),
        propValue: this.patternPreview
      });
    }
    AXIOS.put(`/properties/export/save-list`, this.exportSettings)
      .then((message) => {
        this.databaseExportSettings = message.data;
        this.setDefaultProperties();
        UserActionResultSender.showSuccessFeedback("export.update.success");
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public getPropertyValue(key: string): string {
    const setting = this.databaseExportSettings.find((prop) => prop.propKey === key);
    return setting ? setting.propValue : "";
  }

  public getDefaultDestinationFolder() {
    this.loading = true;
    AXIOS.get('/properties/export/default-folder')
      .then((message) => {
        this.defaultExportPath = message.data;
      })
      .catch(error => {
        UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
      })
      .finally(() => {
        this.loading = false;
      });
  }

  public onPatternUpdate(newPattern: string) {
    this.patternPreview = newPattern;
    this.showDialog = false;
  }

  public onPatternDialogClose() {
    this.showDialog = false;
  }

  get exportTypeFileShort(): string {
    switch (this.exportType) {
      case ExportType.JSON:
      case ExportType.JSON_CUSTOM:
        return "json";
      case ExportType.CSV:
        return "csv";
      case ExportType.XML:
        return "xml";
      default:
        return "";
    }
  }

  public setSeparator(val: string) {
    let enteredSeparator;
    if (typeof val === 'object' && val !== null && 'value' in val) {
      enteredSeparator = (val as { value: string }).value;
    } else {
      enteredSeparator = val;
    }
    this.separator = enteredSeparator;
    this.saveCsvDelimiter()
  }
}
